import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  TrendingUp, TrendingDown, Users, DollarSign, Download, 
  Calendar, Target, Award, Filter, ArrowLeft 
} from 'lucide-react';
import { 
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell 
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

const SuperadminAnalytics = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeRange, setTimeRange] = useState('year');
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    loadAnalytics();
  }, [timeRange]);

  const loadAnalytics = async () => {
    const token = localStorage.getItem('token');
    
    if (!token) {
      navigate('/login');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/superadmin/analytics/overview?time_range=${timeRange}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      setAnalytics(response.data);
      setError('');
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        navigate('/login');
      } else {
        setError('Fehler beim Laden der Analysen');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    const token = localStorage.getItem('token');
    const url = `${API_URL}/api/superadmin/analytics/export?format=${format}&time_range=${timeRange}`;
    
    try {
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `affiliate_analytics_${timeRange}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);
    } catch (err) {
      alert('Fehler beim Export');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  const formatPercent = (percent) => {
    const value = percent || 0;
    const isPositive = value >= 0;
    return (
      <span className={`flex items-center gap-1 ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
        {isPositive ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
        {Math.abs(value).toFixed(2)}%
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !analytics) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-600">{error || 'Keine Daten verfügbar'}</p>
      </div>
    );
  }

  const { overview, conversion_funnel, top_performers, monthly_trends } = analytics;

  // Prepare funnel data for chart
  const funnelData = [
    { name: 'Registrierungen', value: conversion_funnel.total_signups, fill: '#3b82f6' },
    { name: 'Zahlende Kunden', value: conversion_funnel.paying_customers, fill: '#10b981' }
  ];

  return (
    <div className="p-4 sm:p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={() => navigate('/superadmin/affiliates')}
              className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">
                Affiliate Analytics & Insights
              </h1>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                Umfassende Statistiken und Leistungsanalysen
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <div className="flex items-center gap-2 flex-1">
              <Filter className="w-5 h-5 text-gray-500" />
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="flex-1 sm:flex-initial px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
              >
                <option value="month">Monat</option>
                <option value="quarter">Quartal</option>
                <option value="year">Jahr</option>
              </select>
            </div>

            <button
              onClick={() => handleExport('csv')}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 min-h-[44px]"
            >
              <Download className="w-4 h-4" />
              CSV Export
            </button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 sm:p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">
                Gesamt-MRR
              </h3>
              <DollarSign className="w-5 h-5 text-green-500" />
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-1">
              {formatCurrency(overview.total_mrr)}
            </p>
            {formatPercent(overview.mrr_growth)}
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 sm:p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">
                Gesamt-Umsatz
              </h3>
              <TrendingUp className="w-5 h-5 text-blue-500" />
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-1">
              {formatCurrency(overview.total_revenue)}
            </p>
            {formatPercent(overview.revenue_growth)}
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 sm:p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">
                Gesamt-Provisionen
              </h3>
              <DollarSign className="w-5 h-5 text-purple-500" />
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-1">
              {formatCurrency(overview.total_commissions)}
            </p>
            {formatPercent(overview.commission_growth)}
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 sm:p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">
                Aktive Affiliates
              </h3>
              <Users className="w-5 h-5 text-orange-500" />
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-1">
              {overview.active_affiliates}
            </p>
            <p className="text-xs sm:text-sm text-gray-500">
              von {overview.total_affiliates} gesamt
            </p>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6">
          {/* Conversion Funnel */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 sm:p-6">
            <div className="flex items-center gap-2 mb-4">
              <Target className="w-5 h-5 text-blue-600" />
              <h2 className="text-base sm:text-lg font-bold text-gray-900 dark:text-gray-100">
                Conversion Funnel
              </h2>
            </div>
            
            <div className="flex items-center justify-center mb-4">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={funnelData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#3b82f6" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="grid grid-cols-3 gap-2 sm:gap-4 text-center pt-4 border-t border-gray-200 dark:border-gray-700">
              <div>
                <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {conversion_funnel.total_signups}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">Registrierungen</p>
              </div>
              <div>
                <p className="text-lg sm:text-2xl font-bold text-green-600">
                  {conversion_funnel.paying_customers}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">Zahlend</p>
              </div>
              <div>
                <p className="text-lg sm:text-2xl font-bold text-blue-600">
                  {conversion_funnel.conversion_rate.toFixed(1)}%
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">Conversion</p>
              </div>
            </div>
          </div>

          {/* Monthly Trends */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 sm:p-6">
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="w-5 h-5 text-purple-600" />
              <h2 className="text-base sm:text-lg font-bold text-gray-900 dark:text-gray-100">
                Monatlicher Verlauf
              </h2>
            </div>
            
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={monthly_trends}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis dataKey="period" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Line type="monotone" dataKey="revenue" stroke="#3b82f6" strokeWidth={2} name="Umsatz" />
                <Line type="monotone" dataKey="commission" stroke="#10b981" strokeWidth={2} name="Provision" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Performers */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 sm:p-6">
          <div className="flex items-center gap-2 mb-4">
            <Award className="w-5 h-5 text-yellow-500" />
            <h2 className="text-base sm:text-lg font-bold text-gray-900 dark:text-gray-100">
              Top 10 Performer
            </h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase sticky left-0 bg-gray-50 dark:bg-gray-900">#</th>
                  <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">E-Mail</th>
                  <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Umsatz</th>
                  <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Provisionen</th>
                  <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">MRR</th>
                  <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Kunden</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {top_performers.map((performer, index) => (
                  <tr key={performer.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-2 sm:px-4 py-3 sticky left-0 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700">
                      <div className={`flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm ${
                        index === 0 ? 'bg-yellow-100 text-yellow-700' :
                        index === 1 ? 'bg-gray-200 text-gray-700' :
                        index === 2 ? 'bg-orange-100 text-orange-700' :
                        'bg-blue-50 text-blue-600'
                      }`}>
                        {index + 1}
                      </div>
                    </td>
                    <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100">
                      {performer.name}
                    </td>
                    <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm text-gray-600 dark:text-gray-400 hidden sm:table-cell">
                      {performer.email}
                    </td>
                    <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm font-bold text-blue-600 dark:text-blue-400">
                      {formatCurrency(performer.total_revenue)}
                    </td>
                    <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm font-bold text-green-600 dark:text-green-400">
                      {formatCurrency(performer.total_commission)}
                    </td>
                    <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100 hidden md:table-cell">
                      {formatCurrency(performer.mrr)}
                    </td>
                    <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm text-gray-900 dark:text-gray-100 hidden md:table-cell">
                      {performer.customers}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperadminAnalytics;
